import { styled } from "goober";
import { type RefObject, useLayoutEffect, useRef, useState } from "react";
import { caretCoordinates } from "../../lib/caretCoordinates";
import { PressButton } from "../PressButton";
import { Text } from "../Text";
import { type SkillItem } from "./SlashMenu";

const Popover = styled("div")`
  position: absolute;
  z-index: 3;
  width: 140px;
  max-height: 160px;
  overflow-y: auto;
  padding: 4px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 1px;
  background: var(--surface-primary);
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 12px;
`;

const Row = styled(PressButton)`
  display: flex;
  align-items: center;
  width: 100%;
  height: 24px;
  padding: 0 8px;
  border-radius: 8px;
  background: transparent;
  color: var(--text-primary);
  &:hover {
    background: var(--alpha4);
  }
  &[data-active="true"] {
    background: var(--alpha4);
  }
`;

type Props = {
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  slashStart: number;
  skills: readonly SkillItem[];
  selectedIndex: number;
  onSelect: (skill: SkillItem) => void;
};

export function SlashSkillMenu({
  textareaRef,
  value,
  slashStart,
  skills,
  selectedIndex,
  onSelect,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const ta = textareaRef.current;
    const el = ref.current;
    if (!ta || !el) return;
    let raf = 0;
    const place = () => {
      raf = 0;
      const origin = el.offsetParent as HTMLElement | null;
      const orect = origin?.getBoundingClientRect();
      const taRect = ta.getBoundingClientRect();
      const caret = caretCoordinates(ta, slashStart);
      setPos({
        left: taRect.left - (orect?.left ?? 0) + caret.left - ta.scrollLeft,
        top: taRect.top - (orect?.top ?? 0) + caret.top - ta.scrollTop,
      });
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(place);
    };
    place();
    const ro = new ResizeObserver(schedule);
    ro.observe(ta);
    ta.addEventListener("scroll", schedule, { passive: true });
    return () => {
      ro.disconnect();
      ta.removeEventListener("scroll", schedule);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [textareaRef, value, slashStart]);

  useLayoutEffect(() => {
    ref.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  return (
    <Popover
      ref={ref}
      style={{
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        transform: "translateY(calc(-100% - 6px))",
        opacity: pos ? 1 : 0,
      }}
      // Keep the textarea focused so selecting a row doesn't dismiss the menu.
      onMouseDown={(e) => e.preventDefault()}
    >
      {skills.map((skill, index) => (
        <Row
          key={skill.id}
          data-no-pill=""
          data-active={index === selectedIndex ? "true" : undefined}
          onPress={() => onSelect(skill)}
        >
          <Text variant="text-small-md" color="var(--text-primary)" ellipsis>
            {skill.name}
          </Text>
        </Row>
      ))}
    </Popover>
  );
}
